import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Toast } from "primereact/toast";
import { api } from "../../../../../../../../../utility/AxiosInceptor";
import { debounce } from "lodash";
import CommonLoaderBlue from "../../../../../../../../components/Loader/LoaderBlue";

export const AssignLabelNew = ({
  setRefreshCustomerData,
  setVisible,
  toast,
  cpData,
}) => {
  const [orderIdData, setOrderIdData] = useState("");
  const [carrier, setCarrier] = useState([]);
  const [carrierVal, setCarrierVal] = useState("");

  const [services, setServices] = useState([]);
  const [servicesVal, setServicesVal] = useState("");

  const [delivery, setDelivery] = useState([]);
  const [deliveryVal, setDeliveryVal] = useState("");

  const [rates, setRates] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [isShippingLabelDiscount, setIsShippingLabelDiscount] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const deliveryFedEx = [
    { name: "none" },
    { name: "delivery" },
    { name: "signature" },
    { name: "direct_signature" },
    { name: "adult_signature" },
  ];

  // Get order ID data
  const fetchOrderData = useCallback(async () => {
    try {
      const { data } = await api.post(`/api/web/order`, {
        orderNumber: cpData?.enrollmentId,
      });
      setOrderIdData(data?.data);
    } catch (error) {
      console.error("Error fetching order data:", error);
    }
  }, [cpData]);

  // Get carrier list
  const fetchCarriers = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/web/shipment/carrier/carriersList`);
      setCarrier(data?.data || []);
    } catch (error) {
      console.error("Error fetching carriers:", error);
    }
  }, []);

  // Get delivery confirmation list
  const fetchDeliveryOptions = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/api/web/shipment/carrier/getDeliveryConfirmation`,
      );
      setDelivery(data || []);
    } catch (error) {
      console.error("Error fetching delivery options:", error);
    }
  }, []);

  // Get services for selected carrier
  const fetchServices = useCallback(async (carrierCode) => {
    try {
      const { data } = await api.get(
        `/api/web/shipment/carrier/listServices?carrierCode=${carrierCode}`,
      );
      setServices(data?.data || []);
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  }, []);

  const fetchRates = useCallback(async () => {
    if (!carrierVal || !servicesVal || !deliveryVal) return;
    setRatesLoading(true);
    try {
      const { data } = await api.post(`/api/web/shipment/getRates`, {
        serviceprovider: cpData?.serviceProvider,
        carrierCode: carrierVal,
        serviceCode: servicesVal,
        toPostalCode: cpData?.zip,
        toCountry: "US",
        toState: cpData?.state,
        toCity: cpData?.city,
        packageCode: "",
        confirmation: deliveryVal,
        residential: "false",
        inventoryType: cpData?.inventoryId?._id,
      });
      setRates(data?.data || []);
    } catch (error) {
      console.error("Error fetching rates:", error);

      toast.current.show({
        severity: "error",
        summary: "Fetch Rates",
        detail: error?.response?.data?.msg || "Failed to fetch rates.",
      });
    } finally {
      setRatesLoading(false);
    }
  }, [carrierVal, servicesVal, deliveryVal, cpData]);

  // 👇 Wrap fetchRates in debounce
  const debouncedFetchRates = useMemo(
    () => debounce(fetchRates, 500),
    [fetchRates],
  );

  // Example usage inside effect
  useEffect(() => {
    debouncedFetchRates();

    // Cleanup debounce on unmount
    return () => debouncedFetchRates.cancel();
  }, [debouncedFetchRates]);

  // Handle Label Creation
  const handleLabel = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("userData"));
      await api.post(`/api/web/order/createLable`, {
        orderId: orderIdData?.orderId,
        userId: user?._id,
        testLabel: false,
      });
      toast.current.show({
        severity: "success",
        summary: "Create Label",
        detail: "Label Created Successfully.",
      });
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Create Label",
        detail: error?.response?.data?.msg || "Label Creation Failed.",
      });
      // ❌ Important: rethrow so handleSubmit knows it failed
      throw error;
    }
  };

  const handleStoreServices = async () => {
    const dataToSend = {
      customerId: cpData?._id,
      carrierVal: carrierVal,
      servicesVal: servicesVal,
      deliveryVal: deliveryVal,
      labelSubmissionval: false,
      shippingLabelDiscount: isShippingLabelDiscount,
    };
    try {
      await api.post(`/api/user/storeServices`, dataToSend);
      toast.current.show({
        severity: "success",
        summary: "Store Services",
        detail: "Services stored successfully.",
      });
    } catch (error) {
      console.error("Error storing services:", error);
      toast.current.show({
        severity: "error",
        summary: "Store Services",
        detail: "Failed to store services.",
      });
    }
  };
  useEffect(() => {
    return () => {
      localStorage.removeItem("shipmentCost");
      localStorage.removeItem("shipmentName");
    };
  }, []);
  // Create invoice
  const handleSubmit = async () => {
    const user = JSON.parse(localStorage.getItem("userData"));
    const shippingCost = localStorage.getItem("shipmentCost");

    const dataToSend = {
      userId: user?._id,
      enrollmentId: cpData?.enrollmentId,
      accountId: cpData?.accountId,
      amountPaid: isShippingLabelDiscount ? shippingCost : "",
      invoicetype: "Shipping Cost",
      includeTaxes: false,
      invoiceDueDate: `${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}-${new Date().getFullYear()}`, // MM-DD-YYYY
      invoiceOneTimeCharges: shippingCost,
      totalAmount: shippingCost,
      isPaid: isShippingLabelDiscount ? true : false,
    };

    setIsLoading(true);
    try {
      // ✅ First try label
      await handleStoreServices(); // Store services first
      await handleLabel(); // will throw if failed → skips invoice

      // ✅ Only runs if label succeeded

      await api.post(`/api/web/invoices/adHocInvoiceGeneration`, dataToSend);

      setRefreshCustomerData((prev) => !prev);
      setVisible(false);
    } catch (error) {
      console.error("Label or invoice creation failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    fetchOrderData();
    fetchCarriers();
    fetchDeliveryOptions();
  }, [fetchOrderData, fetchCarriers, fetchDeliveryOptions]);

  useEffect(() => {
    if (carrierVal) {
      fetchServices(carrierVal);
      setServicesVal("");
      setDeliveryVal("");
    }
  }, [carrierVal, fetchServices]);

  return (
    <div className="w-full">
      <div className="invoice-download-main flex flex-wrap flex-row justify-content-center">
        <div
          style={{ height: "auto", top: "18rem", width: "100rem" }}
          className="main-download-invoice"
        >
          {/* Checkbox */}

          {/* Dropdowns */}
          <div
            style={{ width: "100rem" }}
            className="top-header-downloadinvoice"
          >
            <h1>Assign Shipping Label & Generate Invoice</h1>
            <p className="w-full">
              Generates a shipping cost invoice with optional label creation
              based on selected carrier, service, and delivery options.
            </p>
          </div>
          <div></div>
          <div className="w-full flex justify-content-between">
            <DropdownField
              label="Carrier"
              value={carrierVal}
              options={carrier}
              optionLabel="name"
              optionValue="code"
              onChange={(e) => setCarrierVal(e.value)}
            />
            <DropdownField
              label="Services"
              value={servicesVal}
              options={services}
              optionLabel="name"
              optionValue="code"
              onChange={(e) => setServicesVal(e.value)}
            />
            <DropdownField
              label="Delivery Confirmation"
              value={deliveryVal}
              options={carrierVal === "FedEx" ? deliveryFedEx : delivery}
              optionLabel={carrierVal === "FedEx" ? "name" : "type"}
              optionValue={carrierVal === "FedEx" ? "name" : "type"}
              onChange={(e) => setDeliveryVal(e.value)}
            />
          </div>
          <div
            className="flex flex-wrap w-full"
            style={{ marginTop: "1.6rem", marginLeft: "0.3rem" }}
          >
            <Checkbox
              checked={isShippingLabelDiscount}
              onChange={(e) => {
                setIsShippingLabelDiscount(e.checked);
              }}
            />
            <p className="shippinglabel">
              &nbsp;Add Shipping Label as Discount
            </p>
          </div>
          {/* Expected Cost */}
          <div
            style={{ gap: "2rem" }}
            className="calendar_field mt-4 flex flex-wrap align-items-center w-full justify-content-start"
          >
            <p
              style={{ width: "auto", marginTop: "-0.1rem" }}
              className="field_label"
            >
              Expected Cost:
              {ratesLoading
                ? ""
                : rates
                    .filter(
                      (item) =>
                        item?.serviceCode === servicesVal &&
                        item?.serviceName.includes("- Package"),
                    )
                    .map((item) => {
                      let rate =
                        Number(item?.shipmentCost || 0) +
                        Number(item?.otherCost || 0);

                      rate = rate.toFixed(2);

                      localStorage.setItem("shipmentCost", rate);
                      localStorage.setItem("shipmentName", item?.serviceName);

                      return <span key={item._id}> ${rate}</span>;
                    })}
            </p>
            {ratesLoading && <CommonLoaderBlue sizehere={12} />}
          </div>

          {/* Buttons */}
          <div
            style={{ gap: "0.4rem" }}
            className="w-full flex flex-wrap justify-content-end"
          >
            <Button
              type="button"
              label="Cancel"
              className="btn"
              onClick={() => setVisible(false)}
            />
            <Button
              disabled={!carrierVal || !servicesVal || !deliveryVal}
              loading={isLoading}
              label="Submit"
              className="btn"
              onClick={handleSubmit}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const DropdownField = ({ label, ...props }) => (
  <div style={{ width: "33%" }}>
    <p className="field_label">
      {label} <span style={{ color: "red" }}>*</span>
    </p>
    <Dropdown
      style={{ paddingLeft: "1rem" }}
      className="w-full dropdown"
      {...props}
      placeholder={`Select ${label}`}
    />
  </div>
);
